import { Node, mergeAttributes } from '@tiptap/core';

// Share-scope marks. Labels and permissions are decided by
// services/reflectionAudienceService; these are only the glyphs.
const SHARE_ICONS = {
  private: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>',
  chaver: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/></svg>',
  leader: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>',
  team: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>',
  write: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>',
  save: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><path d="M17 21v-8H7v8M7 3v5h8"/></svg>',
};
const shareIcon = (k) => SHARE_ICONS[k] || '';

// The block rendered as a bare prompt + textarea, which did not read as a
// question at a glance. Eyebrow + mark, same pattern as NoteBlock's NOTE_ICONS.
const QUESTION_MARK =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<circle cx="12" cy="12" r="9.25"/>' +
  '<path d="M9.4 9.2a2.7 2.7 0 0 1 5.25.9c0 1.8-2.65 2.7-2.65 2.7"/>' +
  '<path d="M12 17.1h.01"/></svg>';
const EYEBROW_TEXT = 'Discussion Question';

/**
 * Stable id for a block that was authored without one.
 *
 * attrs.id is the questionId every saved answer is keyed to. The old fallback
 * was `textarea-${Date.now()}-${random}`, which is a different id on every
 * page load, so an answer saved once and could never be read back. 82 answers
 * from real people were stranded that way before this was found.
 *
 * Hashing the prompt is what ComprehensionCheck and FillInBlank already do,
 * and it is stable for as long as the prompt is. Editing a prompt still moves
 * the id, so a stored attrs.id always wins and remains the right thing for
 * authored content to carry.
 */
const hashCode = (str) => {
  let hash = 0;
  const s = String(str || '');
  for (let i = 0; i < s.length; i += 1) {
    hash = ((hash << 5) - hash) + s.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash).toString(36);
};
const fallbackId = (prompt) => `ta-${hashCode(prompt)}`;



/**
 * TextAreaBlock Extension
 * Creates a saveable text area that can be shared with leaders/org admins
 * Responses are stored in the database per user
 */
/**
 * Build the interactive share control (3-way visibility radios) as real DOM.
 * Mirrors the ProseMirror spec produced by renderHTML so the auto-save wiring
 * in course-view.html (which binds to .share-radio[data-textarea-id]) still works.
 */
function buildShareControlDOM(id) {
  const wrap = document.createElement('div');
  wrap.className = 'text-area-share-control';
  wrap.setAttribute('data-textarea-id', id);
  wrap.style.cssText =
    'display: flex; align-items: center; flex-wrap: wrap; gap: 0.75rem; font-size: 0.875rem;';

  const label = document.createElement('span');
  label.className = 'share-control-label';
  label.style.cssText = 'color: #6b7280; font-weight: 600;';
  label.textContent = 'Who can see this?';
  wrap.appendChild(label);

  const opts = [
    { scope: 'private', cls: 'share-opt', text: 'Only me', icon: 'private', checked: true },
    { scope: 'leader', cls: 'share-opt share-opt-leader', text: 'My leader', icon: 'leader', checked: false },
    { scope: 'team', cls: 'share-opt share-opt-team', text: 'My team', icon: 'team', checked: false },
  ];

  opts.forEach((o) => {
    const optLabel = document.createElement('label');
    optLabel.className = o.cls;
    optLabel.setAttribute('data-scope', o.scope);
    optLabel.style.cssText = 'display:flex; align-items:center; gap:0.3rem; cursor:pointer;';

    const radio = document.createElement('input');
    radio.type = 'radio';
    radio.className = 'share-radio';
    radio.name = `share-${id}`;
    radio.value = o.scope;
    radio.setAttribute('data-textarea-id', id);
    radio.style.cursor = 'pointer';
    if (o.checked) radio.checked = true;

    const span = document.createElement('span');
    span.textContent = o.text;

    optLabel.appendChild(radio);
    optLabel.appendChild(span);
    wrap.appendChild(optLabel);
  });

  return wrap;
}

export const TextAreaBlock = Node.create({
  name: 'textAreaBlock',
  group: 'block',
  // Leaf widget: the interactive DOM (textarea + share radios) is rendered by
  // addNodeView below and is opaque to ProseMirror. It has no editable child
  // content — declaring `content` here would make ProseMirror try to manage the
  // node's interior and redraw it on DOM mutations, which destroys the live
  // <textarea> mid-typing on mobile.
  atom: true,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-id'),
        renderHTML: (attributes) => ({
          'data-id': attributes.id,
        }),
      },
      prompt: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-prompt'),
        renderHTML: (attributes) => ({
          'data-prompt': attributes.prompt,
        }),
      },
      placeholder: {
        default: 'Type your response here...',
        parseHTML: (element) => element.getAttribute('data-placeholder'),
        renderHTML: (attributes) => ({
          'data-placeholder': attributes.placeholder,
        }),
      },
      isShareable: {
        // true, not false. The old default left 429 of 870 authored prompts with
        // no share control at all, which is why a chaver could never be offered
        // on most questions. This only decides whether the CHOICE is shown; the
        // selected scope still defaults to private.
        default: true,
        parseHTML: (element) => element.getAttribute('data-shareable') === 'true',
        renderHTML: (attributes) => ({
          'data-shareable': attributes.isShareable,
        }),
      },
      rows: {
        default: 4,
        parseHTML: (element) => parseInt(element.getAttribute('data-rows') || '4'),
        renderHTML: (attributes) => ({
          'data-rows': attributes.rows,
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="text-area-block"]',
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const prompt = node.attrs.prompt || HTMLAttributes['data-prompt'] || '';
    const id =
      node.attrs.id ||
      HTMLAttributes['data-id'] ||
      fallbackId(prompt);
    const placeholder =
      node.attrs.placeholder || HTMLAttributes['data-placeholder'] || 'Type your response here...';
    // Two names for the same intent: isShareable is what this extension
    // defines, shareWithLeader is what a lot of authored content actually
    // carries. Honouring only the first left 185 blocks silently private.
    const isShareable =
      node.attrs.shareWithLeader === true
        ? true
        : node.attrs.isShareable !== undefined
          ? node.attrs.isShareable
          : HTMLAttributes['data-shareable'] === 'true';
    const rows = node.attrs.rows || parseInt(HTMLAttributes['data-rows'] || '4');

    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'text-area-block',
        'data-id': id,
        'data-prompt': prompt,
        'data-placeholder': placeholder,
        'data-shareable': isShareable,
        'data-rows': rows,
        class: 'text-area-block',
      }),
      [
        'div',
        { class: 'text-area-header' },
        [
          'div',
          { class: 'text-area-eyebrow' },
          ['span', { class: 'text-area-eyebrow-icon' }],
          ['span', {}, EYEBROW_TEXT],
        ],
        ['div', { class: 'text-area-prompt' }, prompt || 'Your Response:'],
      ],
      [
        'textarea',
        {
          class: 'text-area-input',
          placeholder: placeholder,
          rows: rows,
          'data-textarea-id': id,
        },
      ],
      [
        'div',
        { class: 'text-area-footer' },
        ['span', { class: 'text-area-status' }, 'Auto-saves as you type'],
        ...(isShareable
          ? [
              // Three-way visibility control. Course-view adapts the options at
              // bind time to the reader's group context (companions/mentorship/
              // solo hide or relabel options); the plain small-group case shows
              // all three. Values map to shareScope: private | leader | team.
              [
                'div',
                {
                  class: 'text-area-share-control',
                  'data-textarea-id': id,
                  style:
                    'display: flex; align-items: center; flex-wrap: wrap; gap: 0.75rem; font-size: 0.875rem;',
                },
                [
                  'span',
                  { class: 'share-control-label', style: 'color: #6b7280; font-weight: 600;' },
                  'Who can see this?',
                ],
                [
                  'label',
                  {
                    class: 'share-opt',
                    'data-scope': 'private',
                    style: 'display:flex; align-items:center; gap:0.3rem; cursor:pointer;',
                  },
                  [
                    'input',
                    {
                      type: 'radio',
                      class: 'share-radio',
                      name: `share-${id}`,
                      value: 'private',
                      'data-textarea-id': id,
                      checked: 'checked',
                      style: 'cursor:pointer;',
                    },
                  ],
                  ['span', {}, 'Only me'],
                ],
                [
                  'label',
                  {
                    class: 'share-opt share-opt-leader',
                    'data-scope': 'leader',
                    style: 'display:flex; align-items:center; gap:0.3rem; cursor:pointer;',
                  },
                  [
                    'input',
                    {
                      type: 'radio',
                      class: 'share-radio',
                      name: `share-${id}`,
                      value: 'leader',
                      'data-textarea-id': id,
                      style: 'cursor:pointer;',
                    },
                  ],
                  ['span', {}, 'My leader'],
                ],
                [
                  'label',
                  {
                    class: 'share-opt share-opt-team',
                    'data-scope': 'team',
                    style: 'display:flex; align-items:center; gap:0.3rem; cursor:pointer;',
                  },
                  [
                    'input',
                    {
                      type: 'radio',
                      class: 'share-radio',
                      name: `share-${id}`,
                      value: 'team',
                      'data-textarea-id': id,
                      style: 'cursor:pointer;',
                    },
                  ],
                  ['span', {}, 'My team'],
                ],
              ],
            ]
          : [
              [
                'span',
                {
                  class: 'text-area-private-badge',
                  style: 'font-size: 0.875rem; color: #6b7280;',
                },
                'Private (only you can see)',
              ],
            ]),
      ],
    ];
  },

  // Render the block through a NodeView so ProseMirror treats the whole widget
  // as opaque. Without this, the <textarea> produced by renderHTML lives inside
  // ProseMirror's managed contenteditable, and on Android the soft keyboard's
  // backspace / autocomplete mutates that region — ProseMirror sees a mutation
  // it didn't author and redraws (replaces) the atom node, so the text box
  // vanishes mid-typing until the page is reloaded (Ashley Fiore, fb49cbf9).
  addNodeView() {
    return ({ node }) => {
      const attrs = node.attrs || {};
      const prompt = attrs.prompt || '';
      const id = attrs.id || fallbackId(prompt);
      const placeholder = attrs.placeholder || 'Type your response here...';
      const isShareable = attrs.isShareable === true || attrs.shareWithLeader === true;
      const rows = attrs.rows || 4;

      const dom = document.createElement('div');
      dom.className = 'text-area-block';
      dom.setAttribute('data-type', 'text-area-block');
      dom.setAttribute('data-id', id);
      dom.setAttribute('data-prompt', prompt);
      dom.setAttribute('data-placeholder', placeholder);
      dom.setAttribute('data-shareable', String(isShareable));
      dom.setAttribute('data-rows', String(rows));

      const header = document.createElement('div');
      header.className = 'text-area-header';
      const eyebrow = document.createElement('div');
      eyebrow.className = 'text-area-eyebrow';
      const eyebrowIcon = document.createElement('span');
      eyebrowIcon.className = 'text-area-eyebrow-icon';
      eyebrowIcon.innerHTML = QUESTION_MARK;
      const eyebrowText = document.createElement('span');
      eyebrowText.textContent = EYEBROW_TEXT;
      eyebrow.appendChild(eyebrowIcon);
      eyebrow.appendChild(eyebrowText);
      header.appendChild(eyebrow);
      const promptEl = document.createElement('div');
      promptEl.className = 'text-area-prompt';
      promptEl.textContent = prompt || 'Your Response:';
      header.appendChild(promptEl);
      dom.appendChild(header);

      const textarea = document.createElement('textarea');
      textarea.className = 'text-area-input';
      textarea.placeholder = placeholder;
      textarea.rows = rows;
      textarea.setAttribute('data-textarea-id', id);
      // Reflections are prose, not form fields. Keep spellcheck and sentence
      // capitals; refuse AutoFill and password managers, which otherwise offer
      // a name or a street address over somebody's answer.
      textarea.setAttribute('autocomplete', 'off');
      textarea.setAttribute('autocorrect', 'on');
      textarea.setAttribute('autocapitalize', 'sentences');
      textarea.setAttribute('spellcheck', 'true');
      textarea.setAttribute('data-1p-ignore', '');
      textarea.setAttribute('data-lpignore', 'true');
      textarea.setAttribute('data-form-type', 'other');
      dom.appendChild(textarea);

      const footer = document.createElement('div');
      footer.className = 'text-area-footer';
      const status = document.createElement('span');
      status.className = 'text-area-status';
      status.textContent = 'Auto-saves as you type';
      footer.appendChild(status);

      if (isShareable) {
        footer.appendChild(buildShareControlDOM(id));
      } else {
        const badge = document.createElement('span');
        badge.className = 'text-area-private-badge';
        badge.style.cssText = 'font-size: 0.875rem; color: #6b7280;';
        badge.textContent = 'Private (only you can see)';
        footer.appendChild(badge);
      }
      dom.appendChild(footer);

      return {
        dom,
        // The widget manages its own interior. Never let ProseMirror redraw the
        // node from internal DOM mutations, and never let it swallow events that
        // belong to the textarea/radios (keydown, beforeinput, input, click).
        ignoreMutation: () => true,
        stopEvent: () => true,
      };
    };
  },

  addCommands() {
    return {
      setTextAreaBlock:
        (attributes) =>
        ({ commands }) => {
          const id = `textarea-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          return commands.insertContent({
            type: this.name,
            attrs: {
              id,
              ...attributes,
            },
          });
        },
    };
  },
});

export default TextAreaBlock;
